import { lazy, Suspense, useEffect, useState } from "react";
import { BrowserRouter, Navigate, Route, Routes } from "react-router";
import { api } from "../kit/api";
import { ToastProvider } from "../kit/overlays";
import { Boot, Skeleton } from "../kit/ui";
import { MeCtx, type Me } from "./context";
import { Layout } from "./Layout";
import { SignIn } from "../pages/SignIn";
import { Setup } from "../pages/Setup";

const Overview = lazy(() => import("../pages/Overview"));
const Tenants = lazy(() => import("../pages/Tenants"));
const NewTenant = lazy(() => import("../pages/NewTenant"));
const TenantRoutes = lazy(() => import("../pages/tenant/TenantRoutes"));
const Partners = lazy(() => import("../pages/Partners"));
const Partner = lazy(() => import("../pages/Partner"));
const Provisioning = lazy(() => import("../pages/Provisioning"));
const Job = lazy(() => import("../pages/Job"));
const Tickets = lazy(() => import("../pages/Tickets"));
const Feedback = lazy(() => import("../pages/Feedback"));
const PlatformUpdates = lazy(() => import("../pages/PlatformUpdates"));
const Windows = lazy(() => import("../pages/Windows"));
const AgentUpdates = lazy(() => import("../pages/AgentUpdates"));
const Announcements = lazy(() => import("../pages/Announcements"));
const Notifications = lazy(() => import("../pages/Notifications"));
const ServiceStatus = lazy(() => import("../pages/ServiceStatus"));
const Clouds = lazy(() => import("../pages/Clouds"));
const Hosts = lazy(() => import("../pages/Hosts"));
const Host = lazy(() => import("../pages/Host"));
const Team = lazy(() => import("../pages/Team"));
const Roles = lazy(() => import("../pages/Roles"));
const Audit = lazy(() => import("../pages/Audit"));
const Approvals = lazy(() => import("../pages/Approvals"));
const Settings = lazy(() => import("../pages/Settings"));
const QuotesArea = lazy(() => import("../pages/quotes/QuotesArea"));
const Docs = lazy(() => import("../pages/Docs"));

function Signed() {
  const [me, setMe] = useState<Me | null>(null);
  const [error, setError] = useState<Error | null>(null);
  const load = () => (setError(null), api<Me>("/api/me").then(setMe).catch((e) => (e as { status?: number }).status !== 401 && setError(e as Error)));
  useEffect(() => void load(), []);
  if (!me) return <Boot error={error} onRetry={load} />;
  return (
    <MeCtx.Provider value={me}>
      <Layout>
        <Suspense fallback={<div className="page"><Skeleton rows={6} /></div>}>
          <Routes>
            <Route index element={<Overview />} />
            <Route path="tenants" element={<Tenants />} />
            <Route path="tenants/new" element={<NewTenant />} />
            <Route path="tenants/:id/*" element={<TenantRoutes />} />
            <Route path="partners" element={<Partners />} />
            <Route path="partners/:id" element={<Partner />} />
            <Route path="provisioning" element={<Provisioning />} />
            <Route path="provisioning/:id" element={<Job />} />
            <Route path="tickets" element={<Tickets />} />
            <Route path="feedback" element={<Feedback />} />
            <Route path="releases" element={<PlatformUpdates />} />
            <Route path="windows" element={<Windows />} />
            <Route path="agents" element={<AgentUpdates />} />
            <Route path="announcements" element={<Announcements />} />
            <Route path="email" element={<Notifications />} />
            <Route path="docs" element={<Docs />} />
            <Route path="status" element={<ServiceStatus />} />
            <Route path="clouds" element={<Clouds />} />
            <Route path="hosts" element={<Hosts />} />
            <Route path="hosts/:id" element={<Host />} />
            <Route path="team" element={<Team />} />
            <Route path="roles" element={<Roles />} />
            <Route path="audit" element={<Audit />} />
            <Route path="approvals" element={<Approvals />} />
            <Route path="settings" element={<Settings />} />
            <Route path="quotes/*" element={<QuotesArea />} />
            <Route path="*" element={<Navigate to="/" replace />} />
          </Routes>
        </Suspense>
      </Layout>
    </MeCtx.Provider>
  );
}

export function App() {
  return (
    <ToastProvider>
      <BrowserRouter>
        <Routes>
          <Route path="/signin" element={<SignIn />} />
          <Route path="/setup" element={<Setup />} />
          <Route path="/*" element={<Signed />} />
        </Routes>
      </BrowserRouter>
    </ToastProvider>
  );
}
